import React, { useEffect, useState } from "react";
import { ShopingGuideCtaCard } from "@aph/ui-lib-humanx";
import { useRouter } from "next/navigation";
import { fetchUtil } from "@/libs/fetch";
import { NEXT_PUBLIC_PHARMACY_BASE_URL, NEXT_PUBLIC_PHARMACY_MED_IMAGES_BASE_URL } from "@/libs/constants/auth";
import updatingCartSelector from "@/redux/slice/cart/selectors/updatingCart";
import { useAppSelector, useAppDispatch } from "@/redux/hooks";
import { formatPrice } from "@/redux/slice/cart/utils/formatPrice";
import { getSkuQuantity } from "@/redux/slice/cart/utils/getSkuQuantity";
import { cartItemsSelector } from "@/redux/slice/cart/selectors/cartItems";
import { CartItem } from "@/redux/slice/cart/types/CartItem";
import { addCartItem } from "@/redux/slice/cart/utils/addCartItem";
import currentPatientIdSelector from "@/redux/slice/auth/selectors/currentPatientId";
import pharmaAddressDetailsSelector from "@/redux/slice/location/selectors/pharmaAddressDetails";
import { updateCartItem } from "@/redux/slice/cart/utils/updateCartItem";
import { removeCartItem } from "@/redux/slice/cart/utils/removeCartItem";
import { DeviceInfo } from "@/interface/deviceInfo";
import { ProductInfo } from "@/components/ShoppingComponent/types/productInfo";
import ButtonLink from "@/components/ButtonLink";
import styles from "./ShoppingWidget3.module.scss";

interface ShoppingWidget3Props {
  userAgent?: DeviceInfo;
  data?: any;
  productInfo?: ProductInfo;
}

const ShoppingWidget3: React.FC<ShoppingWidget3Props> = (props) => {
  const { userAgent, productInfo, data } = props;
  const [products, setProducts] = useState(null);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const updatingCart = useAppSelector(updatingCartSelector);
  const cartItems = useAppSelector(cartItemsSelector);
  const pharmaAddressDetails = useAppSelector(pharmaAddressDetailsSelector);
  const currentPatientId = useAppSelector(currentPatientIdSelector);

  useEffect(() => {
    const fetchProducts = async () => {
      const response: any = await fetchUtil({
        url: "https://uat.apollopharmacy.in/v1/aplCategoryProducts",
        method: "POST",
        useToken: true,
        token: "Bearer 2o1kd4bjapqifpb27fy7tnbivu8bqo1d",
        ctype: "",
        body: { skus: ["DOL0026", "PAM0289"], page_id: 1, size: 24 },
      });

      if (response?.products) {
        setProducts(response?.products);
      }
    };

    fetchProducts();
  }, []);

  const handleAddItemToCart = (product) => {
    if (product?.is_in_stock) {
      const cartItem: CartItem = {
        ...product,
        quantity: Number(product.moq),
      };

      addCartItem({ itemToAdd: cartItem, pharmaAddressDetails, currentPatientId, dispatch });
    }
  };

  const handleQuantityDecrement = (product) => {
    const medicineQtyInCart = getSkuQuantity(product?.sku, cartItems);
    const cartItem: CartItem = {
      ...product,
      quantity: -1,
    };

    if (medicineQtyInCart === Number(product?.moq)) {
      removeCartItem({ sku: product?.sku, currentPatientId, cartItems, dispatch });
    } else {
      updateCartItem({ itemUpdates: cartItem, cartItems, currentPatientId, dispatch });
    }
  };

  const handleQuantityIncrement = (product) => {
    const medicineQtyInCart = getSkuQuantity(product?.sku, cartItems);

    if (medicineQtyInCart < Number(product?.MaxOrderQty || 10)) {
      const cartItem: CartItem = {
        ...product,
        quantity: 1,
      };
      updateCartItem({ itemUpdates: cartItem, cartItems, currentPatientId, dispatch });
    }
  };

  const getRedirectUrl = (product) => {
    const { is_prescription_required, url_key } = product;

    return Number(is_prescription_required)
      ? `${NEXT_PUBLIC_PHARMACY_BASE_URL}/medicine/${url_key}`
      : `${NEXT_PUBLIC_PHARMACY_BASE_URL}/otc/${url_key}`;
  };

  const handleCardClick = (product) => {
    router.push(getRedirectUrl(product));
  };

  return (
    <>
      <div className="spacingBetweenSections">
        <div className={`${styles.shopingCard2Root} spacingBetweenSections`}>
          {productInfo?.products?.map((product) => {
            const { name = "", price, special_price, sku = "", thumbnail } = product;

            const medicineQtyInCart = getSkuQuantity(sku, cartItems);

            return (
              <ShopingGuideCtaCard
                key={product?.sku}
                title={name}
                imageUrl={`${NEXT_PUBLIC_PHARMACY_MED_IMAGES_BASE_URL}${thumbnail}`}
                originalPrice={formatPrice(price)}
                actualPrice={formatPrice(Number(special_price))}
                ctaText="Add"
                quantity={medicineQtyInCart}
                addCta={() => {
                  handleAddItemToCart(product);
                }}
                decreaseItemQty={() => {
                  handleQuantityDecrement(product);
                }}
                increaseItemQty={() => {
                  handleQuantityIncrement(product);
                }}
                onClick={handleCardClick}
                redirectUrl={getRedirectUrl(product)}
                className={styles.product}
              />
            );
          })}
        </div>
        {data?.ViewAllProductsUrl && (
          <ButtonLink color="tertiarybaby" href={data?.ViewAllProductsUrl} className={styles.btn}>
            Load more products
          </ButtonLink>
        )}
      </div>
    </>
  );
};

export default ShoppingWidget3;
